import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface Project {
  id: string;
  name: string;
  number: string;
  street: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  city?: {
    id: string;
    name: string;
  } | null;
  country?: {
    id: string;
    name: string;
    code: string;
  } | null;
}

interface Country {
  id: string;
  name: string;
  code: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface City {
  id: string;
  name: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
  country?: {
    id: string;
    name: string;
    code: string;
  } | null;
}

interface ProjectDialogProps {
  open: boolean;
  project: Project | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface ProjectFormData {
  name: string;
  number: string;
  street: string;
  countryId: string;
  cityId: string;
  active: boolean;
}

const ProjectDialog: React.FC<ProjectDialogProps> = ({
  open,
  project,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState<ProjectFormData>({
    name: '',
    number: '',
    street: '',
    countryId: '',
    cityId: '',
    active: true
  });
  const [errors, setErrors] = useState<Partial<ProjectFormData>>({});
  const [submitting, setSubmitting] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const dispatch = useAppDispatch();

  const isEditing = !!project;

  // Fetch countries when dialog opens
  useEffect(() => {
    if (open && countries.length === 0) {
      fetchCountries();
    }
  }, [open]);

  // Track the previous countryId to detect actual changes
  const [prevCountryId, setPrevCountryId] = useState<string>('');

  // Reset form when dialog opens/closes or project changes
  useEffect(() => {
    if (open) {
      if (project) {
        console.log('Opening dialog with project:', {
          projectId: project.id,
          countryId: project.country?.id,
          cityId: project.city?.id,
          countryName: project.country?.name,
          cityName: project.city?.name
        });

        const countryId = project.country?.id || '';
        const cityId = project.city?.id || '';

        setFormData({
          name: project.name,
          number: project.number,
          street: project.street,
          countryId: countryId,
          cityId: cityId,
          active: project.active
        });

        setPrevCountryId(countryId);

        // If editing and has a country, fetch cities for that country
        if (countryId) {
          fetchCitiesByCountry(countryId);
        }
      } else {
        setFormData({
          name: '',
          number: '',
          street: '',
          countryId: '',
          cityId: '',
          active: true
        });
        setPrevCountryId('');
        setCities([]);
      }
      setErrors({});
    }
  }, [open, project]);

  // Fetch cities when country changes (only for user manual changes)
  useEffect(() => {
    // Skip if dialog is not open or this is the first load
    if (!open || formData.countryId === prevCountryId) {
      return;
    }

    console.log('Country changed from', prevCountryId, 'to', formData.countryId);

    if (formData.countryId) {
      fetchCitiesByCountry(formData.countryId);
      // Clear cityId when user manually changes country
      setFormData(prev => ({ ...prev, cityId: '' }));
      setPrevCountryId(formData.countryId);
    } else {
      setCities([]);
      setFormData(prev => ({ ...prev, cityId: '' }));
      setPrevCountryId('');
    }
  }, [formData.countryId, open]);

  // Fetch countries function
  const fetchCountries = async () => {
    setLoadingCountries(true);
    try {
      const response = await DataService.get('api/Country/all');
      if (response.ok) {
        const responseData = await response.json();
        let rawCountries: any[] = [];

        if (responseData.success && responseData.data && Array.isArray(responseData.data)) {
          rawCountries = responseData.data;
        } else if (Array.isArray(responseData)) {
          rawCountries = responseData;
        }

        const countriesData: Country[] = rawCountries.map(country => ({
          id: country.id,
          name: country.name,
          code: country.isoCode || country.code,
          isActive: country.active !== undefined ? country.active : country.isActive,
          createdAt: country.createdAt || new Date().toISOString(),
          updatedAt: country.updatedAt || new Date().toISOString()
        }));

        // Only show active countries
        setCountries(countriesData.filter(country => country.isActive));
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Failed to load countries. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setLoadingCountries(false);
    }
  };

  // Fetch cities by country function using the new API endpoint
  const fetchCitiesByCountry = async (countryId: string): Promise<void> => {
    setLoadingCities(true);
    try {
      const response = await DataService.get(`api/City/country/${countryId}`);
      if (response.ok) {
        const responseData = await response.json();
        let rawCities: any[] = [];

        if (responseData.success && responseData.data && Array.isArray(responseData.data)) {
          rawCities = responseData.data;
        } else if (Array.isArray(responseData)) {
          rawCities = responseData;
        }

        const citiesData: City[] = rawCities.map(city => ({
          id: city.id,
          name: city.name,
          active: city.active,
          createdAt: city.createdAt,
          updatedAt: city.updatedAt,
          country: city.country
        }));

        // Filter only active cities
        const filteredCities = citiesData.filter(city => city.active);

        setCities(filteredCities);
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Failed to load cities. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setLoadingCities(false);
    }
  };

  // Handle input changes
  const handleInputChange = (field: keyof ProjectFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'active' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Handle select changes
  const handleSelectChange = (field: keyof ProjectFormData) => (
    event: any
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    // Clear error when user selects
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Validation
  const validateForm = (): boolean => {
    const newErrors: Partial<ProjectFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Project name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Project name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Project name cannot exceed 100 characters';
    }

    if (!formData.number.trim()) {
      newErrors.number = 'Project number is required';
    } else if (formData.number.trim().length < 1) {
      newErrors.number = 'Project number must be at least 1 character';
    } else if (formData.number.trim().length > 50) {
      newErrors.number = 'Project number cannot exceed 50 characters';
    }

    if (formData.street.trim().length > 200) {
      newErrors.street = 'Street cannot exceed 200 characters';
    }

    if (!formData.countryId) {
      newErrors.countryId = 'Country is required';
    }

    if (!formData.cityId) {
      newErrors.cityId = 'City is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        cityId: formData.cityId,
        name: formData.name.trim(),
        number: formData.number.trim(),
        street: formData.street.trim(),
        ...(isEditing && { id: project.id, active: formData.active })
      };

      const endpoint = isEditing ? 'api/Project/update' : 'api/Project/create';
      const response = await DataService.post(endpoint, payload);

      if (response.ok) {
        onSuccess();
      } else {
        // Try to get error message from response
        let errorMessage = `Failed to ${isEditing ? 'update' : 'create'} project`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (e) {
          // Use default error message
        }

        dispatch(
          showAlert({
            message: errorMessage,
            type: 'error'
          })
        );
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: `Network error. Please check your connection and try again.`,
          type: 'error'
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Typography variant="h6" component="div">
            {isEditing ? 'Edit Project' : 'Add New Project'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isEditing
              ? 'Update the project information below'
              : 'Fill in the details to create a new project'
            }
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <TextField
            label="Project Name"
            value={formData.name}
            onChange={handleInputChange('name')}
            error={!!errors.name}
            helperText={errors.name}
            disabled={submitting}
            fullWidth
            placeholder="Enter project name"
            variant="outlined"
          />

          <TextField
            label="Project Number"
            value={formData.number}
            onChange={handleInputChange('number')}
            error={!!errors.number}
            helperText={errors.number}
            disabled={submitting}
            fullWidth
            placeholder="Enter project number"
            variant="outlined"
          />

          <TextField
            label="Street Address"
            value={formData.street}
            onChange={handleInputChange('street')}
            error={!!errors.street}
            helperText={errors.street}
            disabled={submitting}
            fullWidth
            placeholder="Enter street address (optional)"
            variant="outlined"
          />

          <FormControl fullWidth variant="outlined" error={!!errors.countryId}>
            <InputLabel>Country *</InputLabel>
            <Select
              value={formData.countryId}
              onChange={handleSelectChange('countryId')}
              label="Country *"
              disabled={submitting || loadingCountries}
            >
              <MenuItem value="">
                <em>Select a country</em>
              </MenuItem>
              {countries.map(country => (
                <MenuItem key={country.id} value={country.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">
                      {country.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: 'monospace',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        px: 0.5,
                        py: 0.25,
                        borderRadius: 0.5,
                        color: 'text.secondary'
                      }}
                    >
                      {country.code}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {errors.countryId && (
              <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                {errors.countryId}
              </Typography>
            )}
            {loadingCountries && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2, mt: 0.5 }}>
                Loading countries...
              </Typography>
            )}
          </FormControl>

          <FormControl fullWidth variant="outlined" error={!!errors.cityId}>
            <InputLabel>City *</InputLabel>
            <Select
              value={formData.cityId}
              onChange={handleSelectChange('cityId')}
              label="City *"
              disabled={submitting || loadingCities || !formData.countryId}
            >
              <MenuItem value="">
                <em>Select a city</em>
              </MenuItem>
              {cities.map(city => (
                <MenuItem key={city.id} value={city.id}>
                  <Typography variant="body2">
                    {city.name}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
            {errors.cityId && (
              <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                {errors.cityId}
              </Typography>
            )}
            {loadingCities && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2, mt: 0.5 }}>
                Loading cities...
              </Typography>
            )}
            {!formData.countryId && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2, mt: 0.5 }}>
                Please select a country first
              </Typography>
            )}
          </FormControl>

          {isEditing && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.active}
                  onChange={handleInputChange('active')}
                  disabled={submitting}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    {formData.active ? 'Active' : 'Inactive'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formData.active
                      ? 'This project is currently active and available for use'
                      : 'This project is inactive and will not be available for selection'
                    }
                  </Typography>
                </Box>
              }
            />
          )}
        </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleClose}
            disabled={submitting}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} /> : null}
          >
            {submitting
              ? `${isEditing ? 'Updating' : 'Creating'}...`
              : `${isEditing ? 'Update' : 'Create'} Project`
            }
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ProjectDialog;